/**
 * 筋命令适配器。
 */

import { CadCommandType } from '../../../command/CadCommandType';
import { createFeatureReferenceBinding } from '../../../utils/feature/createFeatureReferenceBinding';
import type { FeatureReferenceBinding } from '../../binding/FeatureReferenceBinding';
import { buildRibModelTopologyBindings } from '../../binding/buildRibModelTopologyBindings';
import { RibFeatureDefinitionModel } from '../../definition/rib/RibFeatureDefinition';
import { RibFeaturePrototype } from '../../prototype/rib/RibFeaturePrototype';
import { FeatureCommandAdapter, type FeatureCommandBuildContext } from '../FeatureCommandAdapter';

/**
 * 筋命令适配器。
 */
export class RibCommandAdapter extends FeatureCommandAdapter<RibFeaturePrototype> {
    /**
     * @inheritdoc
     */
    public supports(command: CadCommand): boolean {
        return command.type === CadCommandType.RIB;
    }

    /**
     * @inheritdoc
     */
    public createPrototypeFromCommand(command: CadCommand): RibFeaturePrototype | null {
        if (!this.supports(command)) {
            return null;
        }

        const params = structuredClone(command.params as RibParams);
        const definition = RibFeatureDefinitionModel.fromParams({
            commandParams: params,
        });
        const bindings = this.buildBindingsFromParams(params);
        return new RibFeaturePrototype({
            featureId: command.id,
            definition,
            bindings,
            sourceSketchId: null,
        });
    }

    /**
     * @inheritdoc
     */
    public createCommandFromPrototype(
        context: FeatureCommandBuildContext<RibFeaturePrototype>,
    ): CadCommand {
        const commandParams = context.prototype.getDefinition().getCommandParams();
        return {
            id: context.prototype.getFeatureId(),
            type: CadCommandType.RIB,
            clientId: context.clientId,
            timestamp: context.timestamp ?? Date.now(),
            params: {
                ...commandParams,
                featureKind: 'rib',
                referenceBindings: context.prototype.getBindings().map((binding) => binding.serialize()),
            },
        };
    }

    /**
     * 从命令参数恢复输入绑定。
     *
     * @param params 命令参数。
     * @returns 输入绑定集合。
     */
    private buildBindingsFromParams(params: RibParams): FeatureReferenceBinding[] {
        const serializedBindings = params.referenceBindings ?? buildRibModelTopologyBindings({
            upstreamBodyObjectId: params.upstreamBodyObjectId,
            definition: params.definition,
        });
        return serializedBindings.map((binding) => createFeatureReferenceBinding(binding));
    }
}
